import type { Cadence, SchedulableAuditType } from './cadence';
import type { IndustryCode } from './industry';
import type { TargetsMeta } from './targets';

export type LocationScope = 'worldwide' | 'national' | 'city' | 'address';

/**
 * First-class tracked-competitor relationship row (Competitors feature, plan §2.1).
 * Stored on the brand-queries table: PK=`BRAND#<brandId>`, SK=`COMPETITOR#<domain>`.
 * `domain` is the dedup key shared with the (future) DomainProfile cache. `brand.competitors`
 * (names array) stays in sync as the agent-payload source of truth; these rows add the
 * domain + pinned flag + a zero-migration path to a reverse GSI later.
 */
export interface TrackedCompetitor {
  brandId: string;
  /** Normalized: lowercased, no scheme/path, no leading www. */
  domain: string;
  /** Display name (what appears in `brand.competitors` and matrix columns). */
  name: string;
  /** Pinned competitors always render in the matrix regardless of the top-N cap. */
  pinned?: boolean;
  addedAt: string;
}

/** One Class-B event in a competitor's evolution timeline (plan §2.3). */
export interface CompetitorEvent {
  kind: 'news' | 'website' | 'blog' | 'score' | 'cocite' | 'source' | string;
  title: string;
  detail?: string;
  sourceUrl?: string;
  /** Homepage-diff percentage — `website` events only. */
  diffPct?: number;
  createdAt: string;
}

/** Shared DomainProfile detail (the Class-B side of a competitor). */
export interface CompetitorDomainProfile {
  domain: string;
  name: string;
  /** Reverse-GEO inference (differentiators/targetQueries/contentPillars), when available. */
  profile?: Record<string, unknown> | null;
  lastFetchedAt?: string | null;
  refCount: number;
  /** `ok` = profile enriched; `failed` = scrape/parse failed (site down, parked,
   *  JS-only, 403); `undefined` = never attempted / in progress. */
  profileStatus?: 'ok' | 'failed';
}

/** Response of `GET /brands/:brandId/competitors/:domain/detail`. */
export interface CompetitorDetailResponse {
  profile: CompetitorDomainProfile | null;
  events: CompetitorEvent[];
}

export interface Location {
  locationId: string;
  label: string;
  zone: string;
  scope?: LocationScope;
  /** ISO 3166-1 alpha-2 (lowercase) for the zone's country — set by the
   *  zone-inference LLM at onboarding so the dashboard renders a flag without
   *  re-classifying at read time. Absent on older locations (dashboard falls
   *  back to a deterministic label→country dict in geoFlags.ts). */
  countryCode?: string;
  /** Geographic scale of the zone (village/ville/region/pays/europe/monde) —
   *  the same tag onboarding zone-inference already produces, now persisted. */
  scale?: string;
}

/**
 * Envergure = current geographic footprint / reach, not ambition or age.
 * - hyper-local : one address, neighborhood, or city (neighborhood baker, local artisan)
 * - multi-local : several cities within one region/country (regional franchise, multi-site SMB)
 * - mono-country : full-country presence or notoriety (national retailer, national SaaS)
 * - multi-country : presence across multiple countries (EU-wide B2B, DACH SaaS)
 * - global : worldwide presence without geographic constraint (Accor, BNP, scale-up)
 */
export type Envergure =
  | 'hyper-local'
  | 'multi-local'
  | 'mono-country'
  | 'multi-country'
  | 'global';

/** @deprecated Use Envergure instead. Kept for backward-compat during migration. */
export type BrandMaturity = 'emerging' | 'established' | 'leader';

export interface BrandExistingAssets {
  wikipediaUrl?: string;
  pressKitUrl?: string;
  notableMentions?: string[];
}

/**
 * Audience distribution — how the brand's prospects search, as a funnel from
 * unaware to deliberating. Captured in onboarding (use-case substep) and used
 * at query-generation time to bias prompt PHRASING (mostly referral + authority;
 * narrative is brand-name-anchored and ignores it). Values are percentages that
 * sum to 100 (the UI enforces the invariant via a constrained slider). Optional —
 * absent means "no phrasing bias" (today's behavior).
 */
export interface AudienceMix {
  /** Describes a pain without knowing the product category exists. Highest-funnel. */
  problemAware: number;
  /** Casual, non-technical. Wants a quick recommendation / unique name. */
  recommendation: number;
  /** Knows the product, asks about features and specs. */
  informed: number;
  /** Weighs tradeoffs, explores a decision space across options. */
  evaluative: number;
}

export type AudienceArchetype = keyof AudienceMix;

/**
 * Objective → axis mix. The marketing objective pre-fills a distribution over the
 * three Brand Perception axes; the user can edit it. Used at generation time to
 * split the shared prompt budget (tier `prompts` gauge for self-serve, or the
 * agency-requested total) across axes. Values sum to 100.
 */
export interface ObjectiveMix {
  referral: number;
  authority: number;
  narrative: number;
}

export type BrandObjective = 'leads' | 'ecosystem' | 'image';

/** Objective → default axis weights (pre-fill; editable afterwards). */
export const OBJECTIVE_PRESETS: Record<BrandObjective, ObjectiveMix> = {
  leads: { referral: 60, authority: 20, narrative: 20 },
  ecosystem: { referral: 20, authority: 60, narrative: 20 },
  image: { referral: 20, authority: 20, narrative: 60 },
} as const;

/** List fields whose items can be scoped to one Cibles target (`BrandStrategy.itemTargets`). */
export const STRATEGY_SCOPED_LISTS = [
  'scenarios',
  'keywords',
  'decisionCriteria',
  'differentiators',
  'customerQuestions',
  'customerObjections',
  'avoidTopics',
] as const;

export type StrategyScopedList = (typeof STRATEGY_SCOPED_LISTS)[number];

/** `field → itemText → targetId`. Absent item = all targets. */
export type StrategyItemTargets = Partial<Record<StrategyScopedList, Record<string, string>>>;

/**
 * SK prefix of the per-item scoping rows in the `brand-queries` table (2026-10-11):
 * `PK=BRAND#<brandId>`, `SK=KNOW#<targetId>#<field>#<itemKey>` (itemKey = first 16 hex
 * of sha1(item text)). Every whole-partition reader of that table must skip them.
 */
export const KNOW_SK_PREFIX = 'KNOW#';

/** `GET /brands/:brandId/knowledge/scopes`. */
export interface BrandKnowledgeScopesResponse {
  itemTargets: StrategyItemTargets;
}

/** `GET /brands/:brandId/knowledge?targetId=<id>` — the items scoped to one target. */
export interface BrandKnowledgeTargetItemsResponse {
  items: Array<{ field: StrategyScopedList; text: string }>;
}

/** `POST /brands/:brandId/knowledge/reconcile` — ≤ `TARGET_LIMITS.maxTargetsByIdLookup` ids. */
export interface BrandKnowledgeReconcileRequest {
  targetIds: string[];
}

/** `unassigned` = KNOW rows deleted; `targetIds` = the ids confirmed `notFound`. */
export interface BrandKnowledgeReconcileResponse {
  unassigned: number;
  targetIds: string[];
}

/** Market fields one Cibles marque can override (`BrandStrategy.marqueProfiles`). */
export interface MarqueProfile {
  clientPersonas?: string;
  businessModel?: 'b2b' | 'b2c' | 'both';
  geographicFocus?: string;
}

/**
 * Bound on what a strategy contributes to an LLM prompt. The lists can hold up to
 * `BRAND_STRATEGY_LIMITS` items; a prompt that dumps the strategy keeps the first
 * `maxItemsPerList` of each and never the scoping sidecars (`itemTargets` duplicates
 * every item's text, `marqueProfiles` is not consumed yet). Python agents mirror this
 * constant — keep them in lock-step.
 */
export const STRATEGY_PROMPT_LIMITS = { maxItemsPerList: 30 } as const;

const STRATEGY_PROMPT_DROPPED_FIELDS = ['itemTargets', 'marqueProfiles'] as const;

/** Copy of `strategy` bounded for an LLM prompt (see `STRATEGY_PROMPT_LIMITS`). */
export function strategyForPrompt<T extends Partial<BrandStrategy>>(strategy: T): T {
  const out: Record<string, unknown> = { ...strategy };
  for (const f of STRATEGY_PROMPT_DROPPED_FIELDS) delete out[f];
  for (const [k, v] of Object.entries(out)) {
    if (Array.isArray(v)) out[k] = v.slice(0, STRATEGY_PROMPT_LIMITS.maxItemsPerList);
  }
  const assets = strategy.existingAssets?.notableMentions;
  if (assets) {
    out.existingAssets = {
      ...strategy.existingAssets,
      notableMentions: assets.slice(0, STRATEGY_PROMPT_LIMITS.maxItemsPerList),
    };
  }
  return out as T;
}

export interface BrandStrategy {
  aliases?: string[];
  /**
   * Standardized industry (fixed enum) — drives the cross-tenant authority-cache key
   * `(country, industryCode)` shared by Earned Media + News Watch. Set via a dropdown at
   * brand creation. Was free-text ≤80 chars pre-2026-06; migrated brands keep the old
   * string in `industryLegacy` and the mapped code here.
   */
  industry?: IndustryCode;
  /** @deprecated Original free-text industry, preserved on migration for audit/rollback. */
  industryLegacy?: string;
  clientPersonas?: string;
  businessModel?: 'b2b' | 'b2c' | 'both';
  geographicFocus?: string;
  description?: string;
  differentiators?: string[];
  scenarios?: string[];
  keywords?: string[];
  // PR#1 additions — optional, consumed progressively by agents
  customerQuestions?: string[];
  customerObjections?: string[];
  avoidTopics?: string[];
  /**
   * Critères d'évaluation — what buyers judge the brand on. Image (narrative) axis
   * level-4 key, as `scenarios` is for referral and `keywords` for authority
   * (prompt-studio contract C8). Recorded only for now: no consumer yet.
   */
  decisionCriteria?: string[];
  /**
   * Per-item Cibles scoping of the list fields: `itemTargets[field][itemText] = targetId`.
   * An item with no entry applies to ALL targets. Ids are never propagated from the
   * Cibles side: a reader that cannot resolve an id treats the item as "all", and the
   * next strategy save drops the entry (reconciliation, docs/platform/frontend.md).
   *
   * WRITE-ONLY on the brand row since 2026-10-11: `PATCH /strategy` accepts it as the
   * full desired scoping, stores it as `KNOW#` rows in `brand-queries` and strips it
   * from the row. Read it via `GET /brands/:brandId/knowledge/scopes`. A pre-2026-10-11
   * row may still carry it (legacy; converted on the next save).
   */
  itemTargets?: StrategyItemTargets;
  /**
   * Per-marque overrides of the market fields, keyed by an own Cibles `marque` targetId
   * (2026-10-09). The top-level fields remain the "all marques" value; a marque with no
   * entry inherits them. Same reconciliation as `itemTargets`: an id the by-id lookup
   * reports `notFound` is dropped on the next save.
   */
  marqueProfiles?: Record<string, MarqueProfile>;
  targetLanguages?: string[];
  /**
   * Brand working language — the language in which ALL AI-generated analytical
   * prose is produced (Brand Perception synthesis/recommendations, Earned Media,
   * Content Quality, News Watch). Distinct from `targetLanguages`, which is the
   * measurement dimension (which languages we probe AI engines in). Set ONCE at
   * onboarding (default = creating user's UI language) and IMMUTABLE afterwards —
   * `BrandsService.update` strips it. Only `fr` / `en` are offered today.
   * Absent on pre-2026-07 brands → agents fall back to French (prior behavior).
   */
  workingLanguage?: 'fr' | 'en';
  /** Current geographic footprint. Drives scoring calibration, not query generation. */
  envergure?: Envergure;
  /** @deprecated Replaced by `envergure`. Read-only for existing records. */
  brandMaturity?: BrandMaturity;
  existingAssets?: BrandExistingAssets;
  /** Audience phrasing distribution (sums to 100). Set in onboarding. */
  audienceMix?: AudienceMix;
  /** Objective → axis budget split (sums to 100). Set in onboarding before GEO generation. */
  objectiveMix?: ObjectiveMix;
  /**
   * Agency-only: total number of prompts to generate across all axes. For
   * self-serve tiers the total is the `prompts` entitlement; agency's gauge is
   * unlimited, so the operator enters an explicit budget here. Used only at
   * generation time to split via `objectiveMix`.
   */
  promptBudget?: number;
}

/**
 * Generation-only knobs on BrandStrategy that shape prompt creation but are NOT
 * part of the agent-facing strategy content. Excluded from the validation hash
 * so editing them never flips `strategyMeta`/`geoStrategyMeta` to `stale`.
 */
export const STRATEGY_GENERATION_KNOBS = ['audienceMix', 'objectiveMix', 'promptBudget'] as const;

/**
 * `aliases` only affects deterministic brand-name matching in the Brand
 * Perception scorer (`is_brand_cited` / `brand_is_source` — see scoring.py)
 * and query-generation forbidden-terms — it doesn't change agent-facing
 * strategy content or how agents reason about the brand. Excluded from the
 * validation hash so adding/editing an alias (e.g. via the "Non cité ?" quick
 * fix on a Brand Perception result) never flips `strategyMeta`/
 * `geoStrategyMeta` to `stale` and never requires re-validation.
 */
export const STRATEGY_HASH_EXCLUDED_FIELDS = [...STRATEGY_GENERATION_KNOBS, 'aliases'] as const;

/**
 * Character limits enforced on both frontend (live counter) and backend (validation).
 * Keeps tenant storage predictable and prevents abuse.
 */
export const BRAND_STRATEGY_LIMITS = {
  industry: 80,
  clientPersonas: 500,
  geographicFocus: 200,
  description: 800,
  aliases: { maxItems: 10, maxCharsPerItem: 80 },
  differentiators: { maxItems: 30, maxCharsPerItem: 150 },
  // Raised 2026-10-09 so a brand can carry hundreds of items across the lists. These caps
  // do NOT bound the row on their own (all lists full + itemTargets passes DDB's 400 KB):
  // `assertStrategyFitsRow` in brands.service.ts refuses a save past a byte budget.
  scenarios: { maxItems: 200, maxCharsPerItem: 200 },
  keywords: { maxItems: 200, maxCharsPerItem: 100 },
  decisionCriteria: { maxItems: 200, maxCharsPerItem: 100 },
  customerQuestions: { maxItems: 100, maxCharsPerItem: 200 },
  customerObjections: { maxItems: 100, maxCharsPerItem: 200 },
  avoidTopics: { maxItems: 100, maxCharsPerItem: 150 },
  /** Max length of one `itemTargets` value — a Cibles targetId is a uuid (36). */
  itemTargetIdChars: 64,
  /** Max `marqueProfiles` entries (one per own marque). */
  marqueProfiles: 50,
  targetLanguages: { maxItems: 10, maxCharsPerItem: 10 },
  existingAssets: {
    wikipediaUrl: 500,
    pressKitUrl: 500,
    notableMentions: { maxItems: 20, maxCharsPerItem: 300 },
  },
} as const;

export interface GuardrailViolation {
  field: string;
  message: string;
}

export interface CompetitorEarnedMediaSignal {
  sourceName: string;
  sourceUrl: string;
  snippet: string;
}

export interface CompetitorComparison {
  positioning: string;
  strengths: string[];
  weaknesses: string[];
  sharedTopics: string[];
  uniqueToCompetitor: string[];
}

export interface CompetitorAnalysis {
  name: string;
  url?: string;
  strategy?: Partial<BrandStrategy>;
  earnedMediaSignals: CompetitorEarnedMediaSignal[];
  comparison: CompetitorComparison;
  analyzedAt: string;
}

/**
 * Per-brand Website Audit configuration. Overrides the default Phase A triage behavior.
 *
 * - `includeUrls`: glob patterns — matching URLs bypass utility blocklist + category caps
 *   + budget truncation. Always kept (unless the page doesn't exist at crawl time).
 * - `excludeUrls`: glob patterns — matching URLs are hard-dropped before any other filter,
 *   regardless of category. Useful for "I never want you to audit /my-draft-page" cases.
 *
 * Globs support `*` (any-chars-within-segment), `**` (any-chars-across-segments), `?`
 * (single char). Example: `"**\/jobs/*"` excludes every job sub-page. Patterns are
 * matched against the full URL including protocol + host.
 */
/**
 * Materialized record of an EventBridge Scheduler schedule for one
 * (brand × axis) pair. Written by `ScheduleSyncService.syncBrand` (S3).
 *
 * This is a CACHE — the source of truth is `BrandAuditConfig.cadenceOverride`
 * + tier defaults via `resolveCadence()`. We store the materialized state
 * here so tenant purge + brand delete don't need a `ListSchedules` API call.
 */
export interface BrandSchedule {
  /** EventBridge Scheduler schedule name (deterministic — see ScheduleSyncService). */
  scheduleName: string;
  /** ARN — kept as the source of truth for `DeleteSchedule` calls. */
  scheduleArn: string;
  /** Last cadence written to EventBridge — kept as a cache so the API can compare without an SDK roundtrip. */
  cadence: Cadence;
  /** FlexibleTimeWindow value passed at create time, in minutes. */
  flexibleWindowMinutes: number;
  /** ISO timestamp of the create or last UpdateSchedule call. */
  createdAt: string;
  /**
   * ISO timestamp of the schedule's FIRST fire (EventBridge `StartDate`),
   * anchored one full cadence interval after creation so a freshly-onboarded
   * brand doesn't run the same axis twice on day one (onboarding manual run +
   * an immediate first cron fire). Preserved across re-syncs so cadence edits
   * don't keep pushing it out. Also the anchor for computing the next run date
   * locally (no AWS call) — see `nextRunAt` in cadence.ts.
   */
  firstRunAt?: string;
}

export interface BrandAuditConfig {
  includeUrls?: string[];
  excludeUrls?: string[];
  /**
   * Per-axis cadence overrides. Unset axes fall through to tier defaults.
   * `'off'` explicitly pauses an axis without falling through.
   */
  cadenceOverride?: Partial<Record<SchedulableAuditType, Cadence>>;
  /**
   * Brand-global conversation cadence (lot I2). Unset = persona cadence → tier default
   * (`resolveConversationCadence`). `'off'` = agency « Manuel » only. Not a
   * SchedulableAuditType: no EventBridge schedule, the conversation sweep reads it.
   * Written only by `BrandsService.setConversationCadence` (a nested SET, never the whole map).
   */
  conversationCadence?: Cadence;
  /**
   * Source of truth for which EventBridge Scheduler schedules currently
   * exist. Written by `ScheduleSyncService.syncBrand` (S3). Never read
   * before S3 lands.
   */
  schedules?: Partial<Record<SchedulableAuditType, BrandSchedule>>;
  /**
   * Per-brand watchlist for the Earned Media orchestrator.
   *  - `include`: registered domains forced into every earned-media audit
   *    (Tavily runs an extra include_domains search per entry).
   *  - `exclude`: registered domains never logged for this brand
   *    (homonym filter — e.g. another company sharing the brand's name).
   * Max 20 entries per list, max 64 chars each, lowercased + deduped server-side.
   */
  earnedMediaWatch?: {
    include?: string[];
    exclude?: string[];
  };
  /**
   * Agency-only: per-brand AI engine allowlist, GLOBAL across all 3 Brand
   * Perception axes (narrative/authority/referral). Engine ids match the
   * `engines` allowlist registry (`perplexity` | `openai` | `gemini` | `claude`).
   * Intersected with the tier allowlist by the Trigger Lambda at dispatch, so it
   * only ever NARROWS the roster. Unset → full tier roster (no change to runs).
   * Drives the per-axis credit estimate (prompts × selected-engine-count).
   */
  enginesEnabled?: string[];
  /**
   * Agency-only: per-brand cap on how many pages the Website Audit crawls (1
   * credit / page analyzed). Scopes BOTH the agent's crawl budget (passed as
   * `pages_per_audit` in the SQS payload) AND the credit reserve. Unset → tier
   * default (agency = 100). Clamped server-side to 1..100 (the agent's hard
   * ceiling). The lever an agency uses to bound per-brand website-audit cost.
   */
  maxPagesPerWebsiteAudit?: number;
  /**
   * Agency-only: per-brand cap on how many third-party pages the Earned Media
   * audit fetches + judges (1 credit / fetch). Scopes BOTH the agent's fetch
   * budget (passed as `fetch_budget` in the SQS payload, overriding the
   * all-tiers `TIER_BUDGETS` default of 100) AND the credit reserve. Unset →
   * tier default (agency = 100). Clamped server-side to 1..100. The lever an
   * agency uses to bound per-brand earned-media cost.
   */
  maxFetchPerEarnedMedia?: number;
}

export interface Brand {
  brandId: string;
  tenantId: string;
  name: string;
  url?: string;
  /** Phase A triage overrides — optional per-brand include/exclude URL globs. */
  auditConfig?: BrandAuditConfig;
  /** Email digest config (S5). Per-recipient cadence, materialized to EventBridge Scheduler. */
  emailConfig?: import('./email').BrandEmailConfig;
  /**
   * CloudFront URL to the normalized 64x64 favicon PNG (at `tenants/<t>/brands/<b>/favicon-64.png`).
   * The 128x128 variant is the same path with `-64.png` → `-128.png`. Set lazily
   * by FaviconService — missing means "not yet fetched" and triggers a lazy backfill
   * call from the frontend.
   */
  faviconUrl?: string;
  /** ISO timestamp of the last successful favicon fetch. Used for cache control. */
  faviconFetchedAt?: string;
  locations: Location[];
  competitors: string[];
  strategy?: BrandStrategy;
  /**
   * Validation status + snapshot hash for `strategy`. Absent when no strategy has
   * ever been saved. See [geo-strategy.ts](./geo-strategy.ts) for the state machine.
   */
  strategyMeta?: import('./geo-strategy').BrandStrategyMeta;
  /**
   * Optional GEO action plan derived from `strategy`. Agents gate on the
   * `geoStrategyMeta.status === 'validated'` invariant at runtime.
   */
  geoStrategy?: import('./geo-strategy').GeoStrategy;
  geoStrategyMeta?: import('./geo-strategy').GeoStrategyMeta;
  competitorAnalyses?: Record<string, CompetitorAnalysis>;
  /**
   * ISO timestamp set ONCE when the brand-onboarding wizard is fully finished
   * (strategy + GEO validated, prompts created, first audits fired). Absent =
   * onboarding never completed → the app routes the user back into the wizard to
   * resume (a brand row exists from wizard step 1, long before finish).
   *
   * This is a one-way "did onboarding ever complete" marker. It is NOT cleared
   * when a strategy later goes `stale` — a validated-then-edited brand stays in
   * the app freely (resume-routing keys on this flag, never on `strategyMeta`).
   */
  onboardingCompletedAt?: string;
  /**
   * ISO timestamp stamped by `BrandsService.remove` before its cascade runs. Present
   * on a stored row only when the cascade failed part-way (the row is deleted last,
   * and only on full success) — such a brand is excluded from `GET /brands` and
   * listed by `GET /brands/pending-deletion` so the user can retry the delete.
   */
  deletingAt?: string;
  /**
   * Denormalized targets summary — avoids an S3 read just to render the GeoStrategy
   * card and the dashboard. Written by targets-consolidate on every manifest flip. (Lot 5)
   * Not part of the strategy hash — editing targets must not flip geoStrategyMeta to stale (D17).
   */
  targetsMeta?: TargetsMeta;
  /**
   * 00b DQ3 — 1..31, present ⇔ `auditConfig.conversationCadence` is a `months` cadence. Written
   * by the cadence write only, in the same UpdateItem: SET when the write CHANGES the cadence to
   * `months` (the UTC day of month of the next launch), untouched when it re-writes the SAME
   * `months` cadence, REMOVEd by a non-`months` write. Absent on old brands.
   */
  conversationMonthAnchorDay?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Lot I2 (C17.5, 00b DQ2) — brand-queries table, PK `BRAND#<brandId>`, SK `CSCHED`: the
 * conversation schedule pointers, OFF the brand row (the tenants stream feeds hubspot-sync on
 * every `BRAND#` write, so a per-launch write there would fire a HubSpot sync per launch).
 * Reaped by the brand-delete partition cascade; no `axisBrandSK`, no `ttl`; absent = today.
 */
export interface ConversationSchedulePointers {
  tenantId: string;
  brandId: string;
  /** ISO, written with `lastConversationLaunchAt`, and by a cadence write. */
  nextConversationLaunchAt?: string;
  /**
   * ISO, written by the sweep on every decided SCHEDULED launch, and by `runNow` ONLY when its
   * `consumeDueTick` consumed a due tick; recency-guarded (`< :t`).
   */
  lastConversationLaunchAt?: string;
  /**
   * The tier class the conversation cadence was last decided under, written ONLY by the
   * allowance reconcile (PSCHED.cadenceTier's twin). Absent = 'self_serve'.
   */
  cadenceTier?: 'agency' | 'self_serve';
}

/**
 * Whether a brand has finished onboarding — used by the app's resume-routing
 * (a brand row exists from wizard step 1, long before the wizard finishes, so
 * "a brand exists" is NOT the same as "onboarding done").
 *
 * Primary signal: the write-once `onboardingCompletedAt` marker.
 *
 * Legacy fallback: brands created before the marker shipped have no
 * `onboardingCompletedAt`. Treat them as onboarded if their Brand Strategy was
 * ever validated (`validated` or `stale` — `stale` means "validated, then
 * edited", still a finished brand). This keeps existing customers OUT of the
 * wizard while still catching genuinely-abandoned half-brands (strategy never
 * past `draft`). New brands always get the explicit marker.
 */
export function isOnboardingComplete(
  brand: Pick<Brand, 'onboardingCompletedAt' | 'strategyMeta'>,
): boolean {
  if (brand.onboardingCompletedAt) return true;
  const s = brand.strategyMeta?.status;
  return s === 'validated' || s === 'stale';
}
